import * as Yup from 'yup';
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Typography,
  Avatar,
  Button
} from '@mui/material';
import { makeStyles, styled } from '@mui/styles';
import { LoadingButton } from '@mui/lab';
import { borderColor, Box } from '@mui/system';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import secureLocalStorage from 'react-secure-storage';
import { ToastContainer, toast } from 'material-react-toastify';
import 'material-react-toastify/dist/ReactToastify.css';

const useStyles = makeStyles((theme) => ({
  avatar: {
    border: `3px solid white`,
    width: 80,
    height: 80
  },
  input: {
    display: 'none'
  },
  large: {
    width: 100,
    height: 100,
    border: '0.1px solid lightgray'
  },
  button: {
    borderRadius: '25px',
    backgroundColor: '#00AB55',
    color: 'white',
    paddingLeft: '10px',
    paddingRight: '10px',
    fontSize: '13px',
    textTransform: 'none',
    boxShadow: '0 8px 16px 0 rgb(0 171 85 / 24%)',
    '&:hover': {
      backgroundColor: '#1edb7c'
    }
  },
  info: {
    width: '100%',
    '@media (max-width:768px)': {
      width: '100%'
    }
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '@media (max-width:768px)': {
      width: '100%',
      flexDirection: 'column'
    }
  }
}));

const CssTextField = styled(TextField)({
  '& label.Mui-focused': {
    color: '#f78181'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#f78181'
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'lightgray'
    },
    '&:hover fieldset': {
      borderColor: '#f78181'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#f78181'
    }
  }
});

export default function AddcommercialForm() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [baseImage, setBaseImage] = useState('');
  const [pass2, setPass2] = useState('');
  const [t, i18n] = useTranslation();

  const notifyErrorAddAdmin = (messageError) => {
    toast.error(messageError, {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };

  function convertBase64(file) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  }
  const uploadImage = async (e) => {
    const file = e.target.files[0];
    const base64 = await convertBase64(file);
    setBaseImage(base64);
    setFieldValue('file', base64);
  };
  const hiddenFileInput = React.useRef(null);
  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const notifySuccess = () => {
    toast.success('A new commercial has been created', {
      position: 'bottom-right',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    });
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    username: Yup.string().required('Full name is required'),
    phone: Yup.string().required('Phone is required'),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      phone: '',
      password: '',
      password2: pass2,
      is_active: true,
      is_superuser: false
    },
    validationSchema: LoginSchema,
    onSubmit: (values) => {
      console.log('data', values);
      const API_ENDPOINT = process.env.REACT_APP_BASE_URL;
      const token = localStorage.getItem('access');
      axios({
        method: 'post',
        url: `${API_ENDPOINT}/api/commercial_api_view/`,
        headers: { Authorization: `Bearer ${token}` },
        data: values
      })
        .then((res) => {
          notifySuccess();
          console.log('response', res.data);
        })
        .catch((err) => {
          console.log('err', err);
          if (err.response.status >= 500) {
            notifyErrorAddAdmin('Network Error');
          } else if (err.response.status >= 400 && err.response.status <= 403) {
            notifyErrorAddAdmin('Email or phone number already bound to an existing account.');
          } else {
            notifyErrorAddAdmin(err.message);
          }
        });
    }
  });

  const { errors, touched, values, isSubmitting, getFieldProps, setFieldValue } = formik;

  useEffect(() => {
    setFieldValue('password2', formik.values.password);
  }, [formik.values.password]);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" className={classes.form} noValidate onSubmit={formik.handleSubmit}>
        <ToastContainer />
        <Stack spacing={3} className={classes.info}>
          <CssTextField
            fullWidth
            required
            autoComplete="fullname"
            type="text"
            placeholder={t('NometPrénom')}
            {...getFieldProps('username')}
            error={Boolean(touched.username && errors.username)}
            helperText={touched.username && errors.username}
          />
          <CssTextField
            fullWidth
            required
            autoComplete="email"
            type="email"
            placeholder={t('email')}
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />
          <CssTextField
            fullWidth
            required
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            placeholder={t('password')}
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} color="#F78181" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          <CssTextField
            fullWidth
            required
            type="number"
            placeholder={t('phone')}
            {...getFieldProps('phone')}
            error={Boolean(touched.phone && errors.phone)}
            helperText={touched.phone && errors.phone}
          />
          <Box textAlign="right" justifyContent="space-between">
            <Button
              style={{ maxWidth: '100px', backgroundColor: '#F78181' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {t('ajouter')}
            </Button>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
